import React from 'react';
import { Link } from 'react-router-dom';
import { Target, TrendingUp, Heart, BookOpen, ArrowRight } from 'lucide-react';
import { Button } from '@/components/ui/button';

export default function Philosophy() {
  return (
    <div className="pt-20 min-h-screen" style={{backgroundColor: "#2C3540"}}>
      {/* Hero Section */}
      <section className="relative py-20 overflow-hidden" style={{backgroundColor: '#1F2937'}} data-testid="philosophy-hero">
        <div className="absolute inset-0 bg-gradient-to-b from-background-paper to-background"></div>
        <div className="relative z-10 max-w-4xl mx-auto px-4 sm:px-6 lg:px-8 text-center">
          <h1 className="font-heading text-5xl md:text-7xl font-bold text-text-primary mb-6">
            Our Philosophy
          </h1>
          <p className="text-xl md:text-2xl text-secondary font-semibold">
            Your goals are my mission
          </p>
        </div>
      </section>

      {/* Main Content */}
      <section className="py-16" style={{backgroundColor: '#2C3540'}} data-testid="philosophy-content">
        <div className="max-w-4xl mx-auto px-4 sm:px-6 lg:px-8">
          <div className="glass-effect p-10 md:p-16 rounded-2xl space-y-12 blog-content">
            
            <div className="space-y-4">
              <p className="text-lg text-text-secondary leading-relaxed">
                I started my investment journey in 2017 and quickly realized how proper guidance can change someone’s financial future.
              </p>
            </div>

            <div className="space-y-6">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary">
                What I Do
              </h2>
              <p className="text-lg text-text-secondary leading-relaxed">
                I don’t just manage investments - I transform financial dreams into reality. My work focuses on:
              </p>
              <ul className="space-y-3 text-lg text-text-secondary">
                <li className="flex items-start">
                  <span className="text-primary mr-3 text-2xl">•</span>
                  <span>Creating clear paths to reach your life goals</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-3 text-2xl">•</span>
                  <span>Protecting your investments by managing risks and behavioral biases</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-3 text-2xl">•</span>
                  <span>Building wealth that grows steadily over time</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-3 text-2xl">•</span>
                  <span>Teaching you how money can work harder for you</span>
                </li>
                <li className="flex items-start">
                  <span className="text-primary mr-3 text-2xl">•</span>
                  <span>Being your guide in your financial decisions</span>
                </li>
              </ul>
            </div>

            <div className="space-y-8 border-t border-white/10 pt-8">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary">
                My Approach
              </h2>

              <div className="space-y-6">
                <div className="glass-effect p-6 rounded-xl" data-testid="approach-card">
                  <div className="flex items-start space-x-4">
                    <Target className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-2">Goals First, Numbers Second</h3>
                      <p className="text-text-secondary">
                        The money you invest should have a purpose and it should know what purpose it is serving. I start by understanding what you’re aiming for, then build a path for you to get there.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="glass-effect p-6 rounded-xl" data-testid="approach-card">
                  <div className="flex items-start space-x-4">
                    <TrendingUp className="h-8 w-8 text-secondary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-2">Simple Beats Complex</h3>
                      <p className="text-text-secondary">
                        Investing shouldn’t feel like rocket science. I create simplified investment strategies to achieve your goals. I explain everything in clear and simple terms so you can understand and trust.
                      </p>
                    </div>
                  </div>
                </div>

                <div className="glass-effect p-6 rounded-xl" data-testid="approach-card">
                  <div className="flex items-start space-x-4">
                    <Heart className="h-8 w-8 text-primary flex-shrink-0 mt-1" />
                    <div>
                      <h3 className="text-xl font-semibold text-text-primary mb-2">Behavior Matters</h3>
                      <p className="text-text-secondary">
                        The biggest investment mistakes aren’t about picking the wrong fund - they’re about making emotional decisions. I’m here to keep you steady when markets aren’t.
                      </p>
                    </div>
                  </div>
                </div>
              </div>
            </div>

            <div className="space-y-6 border-t border-white/10 pt-8">
              <h2 className="font-heading text-3xl md:text-4xl font-bold text-text-primary">
                Why Choose Me?
              </h2>
              
              <div className="grid grid-cols-1 md:grid-cols-2 gap-6">
                <div className="glass-effect p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-primary mb-2">Ethical Practice</h3>
                  <p className="text-text-secondary">Your interests always come first</p>
                </div>
                
                <div className="glass-effect p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-primary mb-2">Goal-Based Planning</h3>
                  <p className="text-text-secondary">Your money works toward your real-life goals</p>
                </div>
                
                <div className="glass-effect p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-primary mb-2">Long-Term Vision</h3>
                  <p className="text-text-secondary">Building wealth takes time - I’m here with you in the journey</p>
                </div>
                
                <div className="glass-effect p-6 rounded-xl">
                  <h3 className="text-lg font-semibold text-primary mb-2">Continuous Learning</h3>
                  <p className="text-text-secondary">Always updating my knowledge through books and research and keep track of market situation</p>
                </div>
              </div>
            </div>

            <div className="pt-8 text-center border-t border-white/10">
              <p className="text-xl text-text-secondary mb-6">
                Let’s talk about where you want to go and how we can get there.
              </p>
              <Link to="/contact">
                <Button className="btn-primary text-lg px-8 py-4" data-testid="philosophy-contact-button">
                  Fill up a Short Form <ArrowRight className="ml-2 h-5 w-5" />
                </Button>
              </Link>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
}